/* eslint-disable max-lines-per-function */
import { useCallback, useState } from 'react';

import { EwusPatient } from '@dreryk/egabinet-ewus-frontend-api';

import { dispatchSnackbarPositiveEvent, StyledFlex, StyledMaxWidth } from '@dreryk/react-components';
import { FormContextValues } from '@dreryk/react-common';

import { downloadPersonalDataFromEwusTranslationKeys } from '../../../translations';
import { PersonalDataFormBeanPart } from '../../PersonalDataFormSection';

import { EwusFetchErrorNotification } from './EwusFetchErrorNotification';
import { EwusPatientDownloadLoader } from './EwusPatientDownload.loader';
import { EwusPatientDownloadButton } from './EwusPatientDownloadButton';
import { StyledEwusPatientDownloadTooltip } from './EwusPatientDownloadTooltip.styled';
import { PatientReplaceWithEwusPatient } from './PatientReplaceWithEwusPatient';

type EwusPatientDownloadProps = Pick<
   FormContextValues<PersonalDataFormBeanPart>,
   'register' | 'watch' | 'setValue' | 'formState'
>;

export const EwusPatientDownload = (props: EwusPatientDownloadProps): JSX.Element | null => {
   const [ewusPatient, setEwusPatient] = useState<EwusPatient | undefined>(undefined);
   const currentPatient = props.watch(['firstName', 'lastName', 'pesel']);

   const handleEwusPatientDownloadSuccess = useCallback(
      (patient: EwusPatient | undefined) => {
         if (!currentPatient.firstName && !currentPatient.lastName) {
            dispatchSnackbarPositiveEvent(downloadPersonalDataFromEwusTranslationKeys.downloadedPatientFromEwus);
            setEwusPatient(patient);
            return;
         }
         if (
            currentPatient.firstName === patient?.firstName?.asString() &&
            currentPatient.lastName === patient?.lastName?.asString()
         ) {
            dispatchSnackbarPositiveEvent(downloadPersonalDataFromEwusTranslationKeys.downloadedPatientFromEwus);
            return;
         }
         setEwusPatient(patient);
      },
      [setEwusPatient, currentPatient]
   );

   const handlePatientReplaceWithEwusPatient = useCallback(
      (ewusPatient: EwusPatient) => {
         props.setValue('firstName', ewusPatient.firstName?.asString() ?? null, {
            shouldValidate: true,
            shouldDirty: true,
         });
         props.setValue('lastName', ewusPatient.lastName?.asString() ?? null, {
            shouldValidate: true,
            shouldDirty: true,
         });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.setValue]
   );

   if (!currentPatient.pesel) {
      return null;
   }

   return (
      <>
         <EwusPatientDownloadLoader>
            {({ ewusConfigurationStatus, refetchConfiguration }) => (
               <StyledMaxWidth maxWidth={'172px'}>
                  <StyledFlex direction={'column'}>
                     <StyledEwusPatientDownloadTooltip
                        testId={'missingEwusConfiguration'}
                        disabled={ewusConfigurationStatus !== 'NOT_CONFIGURED'}
                        content={downloadPersonalDataFromEwusTranslationKeys.configureEwus}
                     >
                        <EwusPatientDownloadButton
                           pesel={currentPatient.pesel!}
                           onSuccess={handleEwusPatientDownloadSuccess}
                           disabled={ewusConfigurationStatus !== 'CONFIGURED'}
                        />
                     </StyledEwusPatientDownloadTooltip>
                     {ewusConfigurationStatus === 'FETCH_ERROR' ? (
                        <EwusFetchErrorNotification onTryAgainClick={refetchConfiguration} />
                     ) : null}
                  </StyledFlex>
               </StyledMaxWidth>
            )}
         </EwusPatientDownloadLoader>
         <>
            {ewusPatient ? (
               <PatientReplaceWithEwusPatient
                  ewusPatient={ewusPatient}
                  patient={currentPatient}
                  onPatientReplace={(patient) => {
                     handlePatientReplaceWithEwusPatient(patient);
                     setEwusPatient(undefined);
                  }}
                  onPatientReplaceReject={() => {
                     setEwusPatient(undefined);
                  }}
               />
            ) : null}
         </>
      </>
   );
};
